/**
 * Reusable OAuth-popup bridge for "connect" flows (inbox, email warming, CRM).
 *
 * The app runs inside the Shell.js iframe, and after a connect popup round-trips
 * through a cross-origin provider, Chrome's COOP severs `window.opener`. Two
 * consequences this module works around:
 *   1. The popup's `window.opener.postMessage` can be dropped — so the popup
 *      fans the result out over THREE channels (postMessage + BroadcastChannel
 *      + localStorage); the opener subscribes to all three. BroadcastChannel /
 *      storage survive COOP because the final callback page is same-origin as
 *      the opener.
 *   2. The popup can no longer close itself (`window.close()` is refused by
 *      Chrome once the opener link is severed). So the OPENER closes it via the
 *      window handle it still owns from `window.open(...)`.
 *
 * Usage (opener):
 *   const { stop } = openConnectPopup({
 *     url, types: ["ZOHO_CONNECT_OAUTH"], onResult: (msg) => {...}
 *   });
 *
 * Usage (popup callback page):
 *   notifyOpener({ code: "200", type: "ZOHO_CONNECT_OAUTH" });
 */

const STORAGE_KEY = "sg_oauth_connect_result";
const CHANNEL_NAME = "sg_oauth_connect";

/**
 * Popup-side: send the result to the opener over all three channels.
 * `message` must carry a `type` so the opener can filter for its own flow.
 */
export const notifyOpener = (message = {}) => {
  const payload = { ...message, _ts: Date.now() };
  try {
    if (window.opener) window.opener.postMessage(payload, "*");
  } catch (e) { /* opener severed by COOP — other channels cover it */ }
  try {
    const bc = new BroadcastChannel(CHANNEL_NAME);
    bc.postMessage(payload);
    bc.close();
  } catch (e) { /* noop */ }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) { /* noop */ }
};

/** Clear the storage signal so a stale value doesn't fire on the next attach. */
const clearConnectSignal = () => {
  try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
};

/**
 * Opener-side: subscribe to a connect result on all three channels, filtered by
 * `types` (array of `type` strings; empty/omitted = accept any). Dedupes replays
 * via `_ts`. Returns a `stop()` to unsubscribe.
 */
const listenForResult = ({ types = [], onResult } = {}) => {
  if (typeof onResult !== "function") return () => {};

  let lastTs = 0;
  const accepts = (msg) => msg && types.includes(msg.type);

  const handle = (msg) => {
    if (!accepts(msg)) return;
    if (msg._ts && msg._ts === lastTs) return; // dedup BC/storage replays
    if (msg._ts) lastTs = msg._ts;
    onResult(msg);
  };

  const onMessage = (ev) => handle(ev?.data);
  window.addEventListener("message", onMessage);

  let bc;
  try {
    bc = new BroadcastChannel(CHANNEL_NAME);
    bc.onmessage = (ev) => handle(ev?.data);
  } catch (e) { /* noop */ }

  const onStorage = (ev) => {
    if (ev.key !== STORAGE_KEY || !ev.newValue) return;
    try { handle(JSON.parse(ev.newValue)); } catch (e) { /* noop */ }
  };
  window.addEventListener("storage", onStorage);

  // The popup may have written its result before this listener attached.
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?._ts && Date.now() - parsed._ts < 30000) handle(parsed);
    }
  } catch (e) { /* noop */ }

  return () => {
    window.removeEventListener("message", onMessage);
    window.removeEventListener("storage", onStorage);
    try { if (bc) bc.close(); } catch (e) { /* noop */ }
  };
};

/**
 * Opener-side: open a centered connect popup, listen for its result, and close
 * the popup from here once the result arrives (reliable even when the popup
 * can't close itself). Also tears down if the user closes the popup manually,
 * with a safety timeout to garbage-collect listeners.
 */
export const openConnectPopup = ({ url, name = "Salesgear", types = [], onResult } = {}) => {
  const left = window.screen.width / 2 - 300;
  const top = window.screen.height / 2 - 400;
  const params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=800,left=${left},top=${top}`;
  const popup = window.open(url, name, params);

  let stopped = false;
  let poll;
  let timer;
  let stopListen = () => {};

  const stop = () => {
    if (stopped) return;
    stopped = true;
    stopListen();
    if (poll) clearInterval(poll);
    if (timer) clearTimeout(timer);
  };

  stopListen = listenForResult({
    types,
    onResult: (msg) => {
      try { if (popup && !popup.closed) popup.close(); } catch (e) { /* noop */ }
      clearConnectSignal();
      try { onResult && onResult(msg); } catch (e) { /* noop */ }
      stop();
    }
  });

  // Clean up if the user closes the popup manually.
  if (popup) {
    poll = setInterval(() => {
      try { if (popup.closed) stop(); } catch (e) { /* noop */ }
    }, 500);
  }
  // Safety net so listeners don't linger if no result ever arrives.
  timer = setTimeout(stop, 180000);
};
